import { createContext, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import PropTypes from "prop-types";
import queryString from "query-string";

import { THEMES, useEditorTheme } from "components/elements";
import { initialFile } from "components/form/fields/UploaderField";
import { useAppsByIds } from "hooks/applications";
import { useManifest } from "hooks/manifests";
import { useProviders } from "hooks/tivoApps";
import { APP_TYPES } from "pages/Applications/constants";
import { base64ToText, textToBase64, urlToBase64 } from "utils/converters";

import {
  BOOL_FIELDS_CONVERSION,
  ENCODED_FIELDS_KEYS,
  FIELDS,
  IMAGE_FIELDS_KEYS,
} from "./ManifestForm/constants";

export const EditorThemeContext = createContext({
  editorTheme: THEMES.dark,
  toggleTheme: () => {},
});

export const ManifestDataContext = createContext({});

export function adaptAttributesToForm(attributes) {
  // shallow copy
  const formData = { ...attributes };
  // null to empty string (we don't want to have uncontrolled inputs)
  Object.keys(formData).forEach((key) => {
    if (formData[key] === null) {
      formData[key] = FIELDS[key]?.type === "array" ? [] : "";
    }
  });
  BOOL_FIELDS_CONVERSION.forEach((key) => {
    if (typeof formData[key] === "boolean") {
      formData[key] = JSON.stringify(formData[key]);
    }
  });
  ENCODED_FIELDS_KEYS.forEach((key) => {
    if (formData[key]) {
      formData[key] = base64ToText(formData[key]);
    }
  });
  for (const key of IMAGE_FIELDS_KEYS) {
    formData[key] = initialFile(formData[key] || "", 0);
  }
  return formData;
}

export async function adaptAttributesToRequest(attributes) {
  // shallow copy
  const requestData = { ...attributes };
  // empty strings and arrays to undefined, as backend does not like empty data
  Object.keys(requestData).forEach((key) => {
    if (
      requestData[key] === "" ||
      (requestData[key] && requestData[key].length === 0)
    ) {
      delete requestData[key];
    }
  });
  BOOL_FIELDS_CONVERSION.forEach((key) => {
    if (requestData[key]) {
      requestData[key] = JSON.parse(requestData[key]);
    } else {
      delete requestData[key];
    }
  });
  ENCODED_FIELDS_KEYS.forEach((key) => {
    if (requestData[key]) {
      requestData[key] = textToBase64(requestData[key]);
    }
  });
  for (const key of IMAGE_FIELDS_KEYS) {
    const notPlaceholderValue = requestData[key]?.url;
    if (notPlaceholderValue) {
      requestData[key] = await urlToBase64(notPlaceholderValue);
    } else {
      delete requestData[key];
    }
  }
  return requestData;
}

const prepareApp = ({ appId, apps = [], tivoapps = [] } = {}) => {
  const [app] = apps;
  if (app) {
    return { id: app.id, type: app.type, mode: "vewd" };
  }
  const [tivoapp] = tivoapps;
  // tivoapps will have value for appId=""
  const mode = tivoapp?.providerId === appId ? "tivo" : "external";

  return {
    id: mode === "tivo" ? tivoapp.providerId : appId,
    type: APP_TYPES.HTML,
    mode,
  };
};

export const ManifestData = ({ id, children }) => {
  const { editorTheme, toggleTheme } = useEditorTheme();
  const location = useLocation();
  // Capture initialAppId once on mount, it's removed from the URL when the user switches tabs
  const initialAppIdRef = useRef(
    queryString.parse(location.search)?.initialAppId || ""
  );
  const { data: manifestData, ...manifestHelpers } = useManifest(id, {
    show_deleted: true,
  });
  const manifest = manifestData?.results;
  const appId = useMemo(
    () => manifest?.provider_id || initialAppIdRef.current || "",
    [manifest]
  );

  const hasAppId = Boolean(appId);
  const { data: apps, ...appsHelpers } = useAppsByIds([appId], hasAppId);
  const { data: tivoApps, ...tivoappsHelpers } = useProviders({
    providerId: appId,
    limit: 1,
    page: 1,
  });

  // When there's no appId, we intentionally skip the app/tivo loading states
  const loading =
    manifestHelpers.isLoading ||
    (hasAppId && (tivoappsHelpers.isPending || appsHelpers.isPending));

  const app = prepareApp({
    apps: apps?.results,
    tivoapps: tivoApps?.results,
    appId,
  });

  return (
    <ManifestDataContext.Provider
      value={{
        app,
        loading,
        manifest,
        onRefetch: manifestHelpers.refetch,
      }}
    >
      <EditorThemeContext.Provider value={{ editorTheme, toggleTheme }}>
        {children}
      </EditorThemeContext.Provider>
    </ManifestDataContext.Provider>
  );
};

ManifestData.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
};
